import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import type { NewsletterSubscriber, PageProps } from "@/types/inertia"

type Props = PageProps & {
  subscribers: NewsletterSubscriber[]
  counts: { confirmed: number; pending: number; unsubscribed: number }
  audience_configured: boolean
}

const STATUS_TONES: Record<string, "accent" | "neutral" | "signal"> = {
  confirmed: "accent",
  pending: "signal",
  unsubscribed: "neutral",
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

export default function NewsletterIndex() {
  const { props } = usePage<Props>()
  const { subscribers, counts, audience_configured } = props
  const flash = props.flash
  const signupVisible = props.features.newsletter_signup

  function deleteSubscriber(subscriber: NewsletterSubscriber) {
    if (!window.confirm(`Delete ${subscriber.email} permanently? This also removes them from the Resend audience.`)) return
    router.delete(`/admin/newsletter/${subscriber.id}`)
  }

  return (
    <AdminShell>
      <Head title="Newsletter">
        <meta name="description" content="Weekly-article newsletter subscribers." />
        <meta property="og:title" content="Newsletter" />
        <meta property="og:description" content="Weekly-article newsletter subscribers." />
      </Head>

      <PageHeader
        title="Newsletter"
        description="People signed up to the weekly article email. Confirmed addresses sync to the Resend audience; broadcasts are reviewed and sent from the Resend dashboard."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}

      {/* Feature switch: whether the signup form appears on the public site */}
      <div className="mt-6 rounded-lg border border-hairline bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Switch
            checked={signupVisible}
            onChange={(enabled) =>
              router.patch("/admin/features/newsletter_signup", { enabled }, { preserveScroll: true })
            }
            label="Signup form on the website"
          />
          <Badge tone={signupVisible ? "accent" : "neutral"}>{signupVisible ? "Shown" : "Hidden"}</Badge>
        </div>
        <p className="mt-2 text-sm text-ink-muted">
          While hidden, the form disappears from Gisela's footer and every article page, and new
          signups are refused. Everyone below stays on the list, and their confirm and unsubscribe
          links keep working.
        </p>
      </div>

      {!audience_configured && (
        <p className="mt-4 rounded-lg bg-surface p-4 text-sm text-ink-muted ring-1 ring-hairline">
          Resend audience sync is not configured — signups and confirmations still work, but addresses
          stay local until <code>RESEND_API_KEY</code> and <code>RESEND_AUDIENCE_ID_GISELA</code> are set.
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Confirmed", value: counts.confirmed },
          { label: "Awaiting confirmation", value: counts.pending },
          { label: "Unsubscribed", value: counts.unsubscribed },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg border border-hairline bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-display">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          {subscribers.length === 500 ? "Showing the most recent 500 subscribers." : ""}
        </p>
        <Button variant="secondary" size="sm" onClick={() => (window.location.href = "/admin/newsletter/export")}>
          Export CSV
        </Button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Email</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Status</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Language</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Signed up</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {subscribers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-muted">
                  {signupVisible
                    ? "No subscribers yet — the signup form is on every article page and in Gisela's footer."
                    : "No subscribers yet — and the signup form is currently hidden on the website."}
                </td>
              </tr>
            )}
            {subscribers.map((subscriber) => (
              <tr key={subscriber.id} className="bg-white transition-colors hover:bg-surface/30">
                <td className="px-4 py-3">
                  <p className="text-ink-body">{subscriber.email}</p>
                  {subscriber.name && <p className="text-xs text-ink-muted">{subscriber.name}</p>}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={STATUS_TONES[subscriber.status] ?? "neutral"}>
                    {subscriber.status.charAt(0).toUpperCase() + subscriber.status.slice(1)}
                  </Badge>
                </td>
                <td className="hidden px-4 py-3 text-ink-muted sm:table-cell">
                  {subscriber.locale === "es" ? "Español" : "English"}
                </td>
                <td className="hidden whitespace-nowrap px-4 py-3 tabular-nums text-ink-muted sm:table-cell">
                  {formatDate(subscriber.created_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="secondary" size="sm" onClick={() => deleteSubscriber(subscriber)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  )
}
